import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient }        from '@angular/common/http';
import { Subscription }      from 'rxjs';
import { SignalRService }    from 'src/app/core/services/signalr.service';
import { environment }       from 'src/app/environments/environment';
import { AiInsightsService } from '../../services/ai-insights.service';

interface DashStats {
  jobs:          number;
  workers:       number;
  logsToday:     number;
  workersOnline: number;
}

interface LiveWorker {
  userId:     string;
  workerName: string;
  latitude:   number;
  longitude:  number;
  timestamp:  string;
  isOnline:   boolean;
}

interface Activity {
  userName:   string;
  jobTitle:   string;
  hoursSpent: number;
  createdUtc: string;
}

// ✅ FEATURE 7 — High severity incident alert shape
interface HighSeverityAlert {
  id:           string;
  type:         string;
  severity:     string;
  severityLabel: string;
  title:        string;
  description:  string;
  location:     string;
  reporterName: string;
  reportedAt:   string;
  category:     string;
  message:      string;
}

@Component({
  selector:    'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls:   ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnDestroy {

  today = new Date();

  // ── Stats ─────────────────────────────────────────────
  stats:        DashStats | null = null;
  loadingStats  = true;

  // ── Live Workers ──────────────────────────────────────
  liveWorkers:  LiveWorker[] = [];
  pagedWorkers: LiveWorker[] = [];
  loadingWorkers = true;
  workerPage     = 1;
  readonly workersPerPage = 5;

  get totalWorkerPages(): number {
    return Math.max(1, Math.ceil(this.liveWorkers.length / this.workersPerPage));
  }

  // ── Recent Activity ───────────────────────────────────
  activities:       Activity[] = [];
  pagedActivities:  Activity[] = [];
  loadingActivity   = true;
  activityPage      = 1;
  readonly activitiesPerPage = 8;

  get totalActivityPages(): number {
    return Math.max(1, Math.ceil(this.activities.length / this.activitiesPerPage));
  }

  // ── AI Insights ───────────────────────────────────────
  aiUnreadCount = 0;

  // ── FEATURE 7 — High severity incident alerts ─────────
  highSeverityAlerts: HighSeverityAlert[] = [];
  showAlertPanel      = false;

  refreshing = false;

  private subs: Subscription[] = [];
  private api  = environment.apiUrl;

  constructor(
    private http:      HttpClient,
    private signalR:   SignalRService,
    public  aiService: AiInsightsService
  ) {}

  ngOnInit(): void {
    this.loadStats();
    this.loadLiveWorkers();
    this.loadActivity();
    this.connectRealTime();

    this.subs.push(
      this.aiService.unreadCount$.subscribe(n => {
        this.aiUnreadCount = n;
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }


  // ── Data loading ──

  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadStats();
    this.loadLiveWorkers();
    this.loadActivity();
    this.aiService.load().subscribe();
    setTimeout(() => (this.refreshing = false), 1200);
  }

  loadStats(): void {
    this.loadingStats = true;
    this.http.get<DashStats>(`${this.api}/api/dashboard/stats`).subscribe({
      next:  s  => { this.stats = s; this.loadingStats = false; },
      error: () => { this.loadingStats = false; }
    });
  }

  loadLiveWorkers(): void {
    this.loadingWorkers = true;
    // ✅ FIXED — was pointing at a non-existent endpoint
    // (/api/workers/locations, which returned a 400 error).
    // Now correctly uses the dashboard's own live-workers endpoint,
    // which returns presence-based IsOnline for every worker.
    this.http.get<LiveWorker[]>(`${this.api}/api/dashboard/live-workers`).subscribe({
      next: workers => {
        this.liveWorkers    = workers;
        this.workerPage     = 1;
        this.updatePagedWorkers();
        this.loadingWorkers = false;
      },
      error: () => { this.loadingWorkers = false; }
    });
  }

  updatePagedWorkers(): void {
    const s = (this.workerPage - 1) * this.workersPerPage;
    this.pagedWorkers = this.liveWorkers.slice(s, s + this.workersPerPage);
  }

  loadActivity(): void {
    this.loadingActivity = true;
    this.http.get<Activity[]>(`${this.api}/api/dashboard/activity`).subscribe({
      next: data => {
        this.activities      = data;
        this.activityPage    = 1;
        this.updatePagedActivities();
        this.loadingActivity = false;
      },
      error: () => { this.loadingActivity = false; }
    });
  }

  updatePagedActivities(): void {
    const s = (this.activityPage - 1) * this.activitiesPerPage;
    this.pagedActivities = this.activities.slice(s, s + this.activitiesPerPage);
  }


  // ── SignalR real-time connections ─────────────────────

  private connectRealTime(): void {
    this.signalR.startConnection().then(() => {

      // Worker location updates
      this.signalR.onLocationUpdate((data: any) => {
        const idx = this.liveWorkers.findIndex(w => w.userId === data.userId);
        if (idx >= 0) {
          this.liveWorkers[idx] = {
            ...this.liveWorkers[idx],
            latitude:  data.latitude,
            longitude: data.longitude,
            timestamp: data.timestamp,
            isOnline:  true
          };
        } else {
          this.liveWorkers.unshift({
            userId:     data.userId,
            workerName: data.workerName ?? 'Worker',
            latitude:   data.latitude,
            longitude:  data.longitude,
            timestamp:  data.timestamp,
            isOnline:   true
          });
        }
        this.updatePagedWorkers();
        if (this.stats)
          this.stats.workersOnline =
            this.liveWorkers.filter(w => w.isOnline).length;
      });

      this.signalR.on('WorkerOnline', (data: any) => {
        const idx = this.liveWorkers.findIndex(w => w.userId === data.userId);
        if (idx >= 0) {
          this.liveWorkers[idx].isOnline = true;
          this.updatePagedWorkers();
        }
        if (this.stats)
          this.stats.workersOnline =
            this.liveWorkers.filter(w => w.isOnline).length;
      });

      this.signalR.on('WorkerOffline', (data: any) => {
        const idx = this.liveWorkers.findIndex(w => w.userId === data.userId);
        if (idx >= 0) {
          this.liveWorkers[idx].isOnline = false;
          this.updatePagedWorkers();
        }
        if (this.stats)
          this.stats.workersOnline =
            this.liveWorkers.filter(w => w.isOnline).length;
      });

      // Job updates
      this.signalR.onJobUpdated(() => {
        this.loadStats();
        this.loadActivity();
      });

      // AI insights
      this.signalR.on('AiInsightCreated', (raw: any) => {
        this.aiService.pushRealTime(raw);
      });

      // ✅ FEATURE 7 — High severity incident alert
      this.signalR.on('HighSeverityIncident', (alert: HighSeverityAlert) => {
        this.onHighSeverityAlert(alert);
      });

    }).catch(err => {
      console.error('Dashboard SignalR error:', err);
    });
  }


  // ── FEATURE 7 — High severity alert handler ───────────

  private onHighSeverityAlert(alert: HighSeverityAlert): void {
    this.highSeverityAlerts = [alert, ...this.highSeverityAlerts];
    this.showAlertPanel     = true;

    try
    {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQoGAACBhYqFbF1fdJiVkHBOU2mBjYV4');
      audio.play().catch(() => {});
    }
    catch { /* sound not critical */ }

    this.loadStats();
  }

  dismissAlert(alertId: string): void {
    this.highSeverityAlerts = this.highSeverityAlerts
      .filter(a => a.id !== alertId);

    if (this.highSeverityAlerts.length === 0)
      this.showAlertPanel = false;
  }

  dismissAllAlerts(): void {
    this.highSeverityAlerts = [];
    this.showAlertPanel     = false;
  }

  navigateToIncidents(): void {
    window.location.href = '/safety';
  }

  get hasHighAlerts(): boolean {
    return this.highSeverityAlerts.length > 0;
  }
}